import React, { useState, useMemo, useRef } from 'react'
import {
  Button,
  colors,
  Divider,
  Expander,
  ExpanderContent,
  ExpanderHeader,
  Grid,
  List,
  Paragraph,
  Row,
} from '@fs/zion-ui'
import { i18n } from '@fs/zion-locale'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { NoticeLoading, PlaceSuggestion } from '@fs/zion-icon'
import Separator from '@fs/zion-ui/dist/cjs/separator'
import getFormattedDate, { dateFormats } from '../../../../../shared/utils/dateFormatHelper'

const removeDefaultListItemStyles = css`
  li {
    list-style: none;
    margin: 0;
    padding: 0;
  }
`

/* we're using an expander instead of a MoreLess because MoreLess works with paragraphs, but not for lists */
function DatesColumn({ dates, place }) {
  const [open, setOpen] = useState(false)
  const { t } = useTranslation()
  const showDates = useMemo(
    () => (
      <>
        {dates?.map(({ fromTimestamp, toTimestamp }) => (
          <Row alignX="end" as="li" guttersY="none" key={`${[fromTimestamp, toTimestamp].filter(Boolean).join('-')}`}>
            <Paragraph size="xs">{getFormattedDate(fromTimestamp, toTimestamp, dateFormats.formatted)}</Paragraph>
          </Row>
        ))}
      </>
    ),
    [dates]
  )

  return (
    <Expander
      open={open}
      setOpen={setOpen}
      ariaControlsId={t('coverageList.ariaControls', 'Date ranges for {place}', { place })}
    >
      <ExpanderContent collapsedHeight={dates?.length > 1 ? 55 : 35}>
        <List>{showDates}</List>
      </ExpanderContent>
      {dates?.length > 2 && (
        <Row alignX="end">
          {/* all the dates in the list are read in screen readers, so the button is aria-hidden to avoid confusion */}
          <Button onClick={() => setOpen(!open)} aria-hidden>
            {open ? t('common-ui:show-less.info.abbr', 'Less') : t('common-ui:show-more.info.abbr', 'More')}
          </Button>
        </Row>
      )}
    </Expander>
  )
}

const disabledTextCss = css`
  color: ${colors.gray30};
`
const SIDESHEET_WIDTH = 327

export default function CoverageList({ coverages, isBottomSheet }) {
  const listRef = useRef()
  const width = isBottomSheet ? listRef.current?.offsetWidth : SIDESHEET_WIDTH
  const isWiderScript = useMemo(() => {
    const widerScriptLanguages = ['zh', 'ko', 'ja']
    return widerScriptLanguages.includes(i18n.language)
  }, [])

  return (
    <div css={removeDefaultListItemStyles}>
      {width && Array.isArray(coverages) ? (
        <List ref={listRef}>
          {coverages.map(({ place, recordTypes }, index) => {
            const name = place?.fullName || place?.name || i18n.t('coverageList.missingPlace', 'Missing place')
            return (
              <li key={place?.id}>
                <Expander initiallyOpen>
                  <ExpanderHeader>
                    {place && (place.published || !Object.keys(place).includes('published')) ? (
                      <span css={!(place.fullName || place.name) ? disabledTextCss : null}>{name}</span>
                    ) : (
                      <Grid gutters="nano">
                        <PlaceSuggestion columns={2} alignY="middle" />
                        <span columns={10}>{name}</span>
                      </Grid>
                    )}
                  </ExpanderHeader>
                  <ExpanderContent>
                    <RecordTypeRowItem
                      recordTypes={recordTypes}
                      width={width}
                      isWiderScript={isWiderScript}
                      fullName={name}
                    />
                  </ExpanderContent>
                </Expander>
                <Divider fullWidth aria-hidden />
                {/* for a thicker divider line */}
                {index !== coverages.length - 1 && <Divider fullWidth aria-hidden />}
              </li>
            )
          })}
        </List>
      ) : (
        // shows a loading indicator instead of letters changing position
        <Row alignX="center">
          <NoticeLoading size="md" />
        </Row>
      )}
    </div>
  )
}

function RecordTypeRowItem({ fullName, recordTypes }) {
  return (
    <ul>
      {recordTypes.map(({ name, date }, i) => (
        <li>
          <Row key={name}>
            <Paragraph size="xs" growX>
              <span style={{ fontWeight: 400 }} css={!name ? disabledTextCss : null}>
                {name || i18n.t('coverageList.missingRecordType', 'Missing record type')}
              </span>
            </Paragraph>
            <DatesColumn place={fullName} dates={date} noShrinkX />
          </Row>
          {!(i === recordTypes.length - 1) && <Divider aria-hidden />}
          <Separator size="nano" aria-hidden />
        </li>
      ))}
    </ul>
  )
}
